import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import VN from "../components/VN";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";

import { Row, Col, Container } from "react-bootstrap";

import {
  TextField,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Paper,
  Divider,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";

import {
  CheckCircleOutline,
  PersonOutline,
  LocationOnOutlined,
  LockOutlined,
  EmailOutlined,
  SearchOutlined,
  ArrowForward,
  VerifiedOutlined,
  PhoneOutlined,
} from "@mui/icons-material";

import { apiRequest } from "../services/apiService";
import { UserDataContext } from "../components/UserDataContext";
import LocationSelect from "../components/LocationSelect";
import PasswordFields from "../components/PasswordFields";
import { API_URL } from "../config/api";
import ReCAPTCHA from "react-google-recaptcha";
import Alerts from "../components/Alerts";

export default function RegisterClient() {
  const { userData } = useContext(UserDataContext);
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [textEmail, setTextEmail] = useState("");
  const [showTextEmail, setShowTextEmail] = useState(false);

  const [textPhone, setTextPhone] = useState("");
  const [showTextPhone, setShowTextPhone] = useState(false);

  const [showEmailVerifyInput, setShowEmailVerifyInput] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState("");

  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);

  const [captchaToken, setCaptchaToken] = useState(null);

  const [apiResponse, setApiResponse] = useState({});
  const [showApiResponse, setShowApiResponse] = useState(false);

  /*
   * ============================================================
   * WALIDACJA
   * ============================================================
   */

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Podaj poprawny adres e-mail")
      .required("Adres e-mail jest wymagany"),

    phone: Yup.string()
      .required("Numer telefonu jest wymagany")
      .length(9, "Numer telefonu musi mieć 9 cyfr"),

    fullName: Yup.string().required("Imię i nazwisko są wymagane"),

    password: Yup.string()
      .min(8, "Hasło musi mieć minimum 8 znaków")
      .matches(/[0-9]/, "Hasło musi zawierać co najmniej jedną cyfrę")
      .matches(
        /[^a-zA-Z0-9]/,
        "Hasło musi zawierać co najmniej jeden znak specjalny",
      )
      .required("Hasło jest wymagane"),

    passwordrepeat: Yup.string()
      .oneOf([Yup.ref("password")], "Hasła muszą być identyczne")
      .required("Powtórz hasło"),

    voivodeship: Yup.string().required("Wybierz województwo"),

    city: Yup.object().nullable().required("Wybierz miasto"),

    rodo: Yup.boolean().oneOf(
      [true],
      "Akceptacja polityki prywatności jest wymagana",
    ),
  });

  /*
   * ============================================================
   * SPRAWDZENIE E-MAILA
   * ============================================================
   */

  useEffect(() => {
    if (!email || !email.includes("@")) {
      setShowTextEmail(false);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const response = await axios.post(API_URL + "/verify-account-email", {
          email,
        });

        if (parseInt(response.data.email) > 0) {
          setTextEmail("Konto z tym adresem e-mail już istnieje.");
          setShowTextEmail(true);
        } else {
          setShowTextEmail(false);
        }
      } catch (error) {
        console.error("Błąd sprawdzania adresu e-mail:", error);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [email]);

  /*
   * ============================================================
   * SPRAWDZENIE TELEFONU
   * ============================================================
   */

  useEffect(() => {
    if (!phone || phone.length !== 9) {
      setShowTextPhone(false);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const response = await axios.post(API_URL + "/verify-account-phone", {
          phone,
        });

        if (parseInt(response.data.phone) > 0) {
          setTextPhone("Konto z tym numerem telefonu już istnieje.");
          setShowTextPhone(true);
        } else {
          setShowTextPhone(false);
        }
      } catch (error) {
        console.error("Błąd sprawdzania numeru telefonu:", error);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [phone]);

  /*
   * ============================================================
   * STYLE
   * ============================================================
   */

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: "#fff",
    },
  };

  /*
   * ============================================================
   * NAGŁÓWEK SEKCJI
   * ============================================================
   */

  const SectionTitle = ({ icon, title, description }) => (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 42,
            height: 42,
            minWidth: 42,
            borderRadius: "50%",
            backgroundColor: "#edf7ed",
            color: "#2e7d32",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          {description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.4,
                lineHeight: 1.5,
              }}
            >
              {description}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );

  /*
   * ============================================================
   * FORMULARZ
   * ============================================================
   */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#faf8f5",
        pb: 8,
      }}
    >
      <VN userData={userData} />

      {showApiResponse && (
        <Row className="sticky-alerts-container">
          <Alerts data={apiResponse} />
        </Row>
      )}

      <Container>
        <Box
          sx={{
            maxWidth: 950,
            mx: "auto",
            pt: {
              xs: 3,
              md: 6,
            },
          }}
        >
          {/* ===================================================
              NAGŁÓWEK STRONY
          =================================================== */}

          <Box
            sx={{
              textAlign: "center",
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 68,
                height: 68,
                borderRadius: "50%",
                bgcolor: "#edf7ed",
                color: "#2e7d32",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <SearchOutlined
                sx={{
                  fontSize: 36,
                }}
              />
            </Box>

            <Typography
              component="h1"
              sx={{
                fontSize: {
                  xs: "1.9rem",
                  md: "2.5rem",
                },
                fontWeight: 800,
                color: "#263238",
                mb: 1.5,
              }}
            >
              Znajdź odpowiednią opiekunkę
            </Typography>

            <Typography
              sx={{
                maxWidth: 680,
                mx: "auto",
                color: "text.secondary",
                fontSize: {
                  xs: "0.95rem",
                  md: "1.05rem",
                },
                lineHeight: 1.7,
              }}
            >
              Załóż bezpłatne konto i znajdź opiekunkę dopasowaną do Twoich
              potrzeb oraz miejsca zamieszkania.
            </Typography>

            {/* ZALETY */}

            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={2}
              justifyContent="center"
              sx={{
                mt: 2.5,
              }}
            >
              <Stack
                direction="row"
                spacing={0.7}
                alignItems="center"
                justifyContent="center"
              >
                <CheckCircleOutline
                  sx={{
                    color: "#2e7d32",
                    fontSize: 19,
                  }}
                />

                <Typography variant="body2">Bezpłatna rejestracja</Typography>
              </Stack>

              <Stack
                direction="row"
                spacing={0.7}
                alignItems="center"
                justifyContent="center"
              >
                <CheckCircleOutline
                  sx={{
                    color: "#2e7d32",
                    fontSize: 19,
                  }}
                />

                <Typography variant="body2">
                  Opiekunki z Twojej okolicy
                </Typography>
              </Stack>

              <Stack
                direction="row"
                spacing={0.7}
                alignItems="center"
                justifyContent="center"
              >
                <CheckCircleOutline
                  sx={{
                    color: "#2e7d32",
                    fontSize: 19,
                  }}
                />

                <Typography variant="body2">Bezpieczne konto</Typography>
              </Stack>
            </Stack>
          </Box>

          {/* ===================================================
              KARTA FORMULARZA
          =================================================== */}

          <Paper
            elevation={0}
            sx={{
              border: {
                xs: "none",
                sm: "1px solid #e5e5e5",
              },

              p: {
                xs: 2,
                sm: 4,
                md: 5,
              },

              boxShadow: {
                xs: "none",
                sm: "0 12px 40px rgba(0,0,0,0.06)",
              },
            }}
          >
            <Formik
              initialValues={{
                email: "",
                phone: "",
                fullName: "",
                password: "",
                passwordrepeat: "",
                voivodeship: "",
                city: null,
                rodo: false,
              }}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setShowApiResponse(false);

                /*
                 * E-MAIL MUSI BYĆ ZWERYFIKOWANY
                 */

                if (!emailVerified) {
                  setApiResponse({
                    title: "Zweryfikuj adres e-mail",
                    severity: "warning",
                    info: "Przed utworzeniem konta potwierdź swój adres e-mail kodem, który wyślemy na Twoją skrzynkę.",
                  });

                  setShowApiResponse(true);
                  setSubmitting(false);
                  return;
                }

                /*
                 * CAPTCHA
                 */

                if (!captchaToken) {
                  setApiResponse({
                    title: "Potwierdź, że nie jesteś robotem",
                    severity: "warning",
                    info: "Zaznacz pole reCAPTCHA przed utworzeniem konta.",
                  });

                  setShowApiResponse(true);
                  setSubmitting(false);
                  return;
                }

                try {
                  const payload = {
                    ...values,
                    city: values.city.id,
                    captcha: captchaToken,
                    accountType: 2,
                    email_verified: emailVerified,
                  };

                  console.log("Payload register-client:", payload);

                  const res = await apiRequest(
                    "register-client",
                    "POST",
                    payload,
                  );

                  console.log("register-client:", res);

                  /*
                   * SUKCES
                   */

                  if (res.error_msg === 200) {
                    setApiResponse({
                      title: "Konto zostało utworzone",
                      severity: "success",
                      info: "Rejestracja zakończyła się pomyślnie. Za chwilę przejdziemy do strony logowania.",
                    });

                    setShowApiResponse(true);

                    setTimeout(() => {
                      navigate("/login", {
                        state: {
                          registered: true,
                        },
                      });
                    }, 5000);

                    return;
                  }

                  /*
                   * E-MAIL NIEZWERYFIKOWANY
                   */

                  if (res.error_msg === 555) {
                    setApiResponse({
                      title: "Adres e-mail nie został zweryfikowany",
                      severity: "warning",
                      info: "Potwierdź adres e-mail kodem i spróbuj ponownie.",
                    });

                    setShowApiResponse(true);
                    return;
                  }

                  /*
                   * CAPTCHA
                   */

                  if (res.error_msg === 403) {
                    setApiResponse({
                      title: "Nie udało się zweryfikować reCAPTCHA",
                      severity: "warning",
                      info: "Odśwież formularz i spróbuj ponownie.",
                    });

                    setShowApiResponse(true);
                    return;
                  }

                  /*
                   * INNY BŁĄD
                   */

                  setApiResponse({
                    title: "Nie udało się utworzyć konta",
                    severity: "error",
                    info: "Wystąpił problem podczas rejestracji. Spróbuj ponownie.",
                  });

                  setShowApiResponse(true);
                } catch (error) {
                  console.error(error);

                  setApiResponse({
                    title: "Nie udało się utworzyć konta",
                    severity: "error",
                    info: "Wystąpił problem podczas rejestracji. Spróbuj ponownie za chwilę.",
                  });

                  setShowApiResponse(true);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                values,
                handleChange,
                setFieldValue,
                errors,
                touched,
                isSubmitting,
              }) => (
                <Form>
                  {/* =========================================
                      DANE KONTAKTOWE
                  ========================================= */}

                  <SectionTitle
                    icon={<PersonOutline />}
                    title="Dane kontaktowe"
                    description="Podaj podstawowe informacje potrzebne do utworzenia konta."
                  />

                  <Row>
                    {/* IMIĘ I NAZWISKO */}

                    <Col xs={12} md={6}>
                      <TextField
                        name="fullName"
                        label="Imię i nazwisko"
                        placeholder="np. Jan Kowalski"
                        fullWidth
                        margin="normal"
                        value={values.fullName}
                        onChange={handleChange}
                        error={touched.fullName && Boolean(errors.fullName)}
                        helperText={touched.fullName && errors.fullName}
                        sx={fieldSx}
                      />
                    </Col>

                    {/* TELEFON */}

                    <Col xs={12} md={6}>
                      <TextField
                        name="phone"
                        label="Numer telefonu"
                        placeholder="123 456 789"
                        fullWidth
                        margin="normal"
                        value={values.phone}
                        onChange={(e) => {
                          const val = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 9);

                          setFieldValue("phone", val);

                          setPhone(val);
                        }}
                        error={
                          (touched.phone && Boolean(errors.phone)) ||
                          showTextPhone
                        }
                        helperText={
                          showTextPhone
                            ? textPhone
                            : touched.phone
                              ? errors.phone
                              : "Numer może być wykorzystywany do kontaktu w sprawie Twojego konta."
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              +48
                            </InputAdornment>
                          ),
                        }}
                        sx={fieldSx}
                      />
                    </Col>
                  </Row>

                  {/* =========================================
                      E-MAIL
                  ========================================= */}

                  <Box
                    sx={{
                      mt: 2,
                      p: {
                        xs: 2,
                        sm: 3,
                      },

                      backgroundColor: emailVerified ? "#f3faf3" : "#fafafa",
                      border: emailVerified
                        ? "1px solid #b7dfb9"
                        : "1px solid #e5e5e5",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{
                        mb: 1,
                      }}
                    >
                      <EmailOutlined
                        sx={{
                          color: emailVerified ? "#2e7d32" : "#555",
                        }}
                      />

                      <Typography
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        Adres e-mail
                      </Typography>

                      {emailVerified && (
                        <VerifiedOutlined
                          sx={{
                            ml: "auto !important",
                            color: "#2e7d32",
                          }}
                        />
                      )}
                    </Stack>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1,
                      }}
                    >
                      Zweryfikujemy adres e-mail, aby zwiększyć bezpieczeństwo
                      Twojego konta.
                    </Typography>

                    <TextField
                      name="email"
                      type="email"
                      label="Adres e-mail"
                      placeholder="np. jan@email.pl"
                      fullWidth
                      margin="normal"
                      disabled={emailVerified}
                      value={values.email}
                      onChange={(e) => {
                        handleChange(e);

                        setEmail(e.target.value);

                        /*
                         * Jeżeli użytkownik
                         * zmienia adres,
                         * resetujemy weryfikację.
                         */

                        setEmailVerified(false);
                        setShowEmailVerifyInput(false);
                        setVerifyMessage("");
                        setEmailCode("");
                      }}
                      error={
                        (touched.email && Boolean(errors.email)) ||
                        showTextEmail
                      }
                      helperText={
                        showTextEmail
                          ? textEmail
                          : touched.email
                            ? errors.email
                            : ""
                      }
                      sx={fieldSx}
                    />

                    {/* E-MAIL ZWERYFIKOWANY */}

                    {emailVerified && (
                      <Alert
                        severity="success"
                        icon={<VerifiedOutlined />}
                        sx={{
                          mt: 1,
                          borderRadius: 2,
                        }}
                      >
                        Adres e-mail został zweryfikowany.
                      </Alert>
                    )}

                    {/* PRZYCISK WYSŁANIA KODU */}

                    {!emailVerified &&
                      values.email &&
                      !showTextEmail &&
                      !showEmailVerifyInput && (
                        <Button
                          type="button"
                          variant="outlined"
                          startIcon={<EmailOutlined />}
                          disabled={sendingCode || Boolean(errors.email)}
                          onClick={async () => {
                            setSendingCode(true);

                            try {
                              await apiRequest(
                                "send-email-code-register",
                                "POST",
                                {
                                  email: values.email,
                                },
                              );

                              setShowEmailVerifyInput(true);

                              setVerifyMessage(
                                "Kod został wysłany. Sprawdź swoją skrzynkę e-mail.",
                              );
                            } catch (error) {
                              console.error(error);

                              setVerifyMessage(
                                "Nie udało się wysłać kodu. Spróbuj ponownie.",
                              );
                            } finally {
                              setSendingCode(false);
                            }
                          }}
                          sx={{
                            mt: 1,
                            textTransform: "none",
                            borderRadius: 2,
                            fontWeight: 600,
                            color: "#2e7d32",
                            borderColor: "#2e7d32",

                            "&:hover": {
                              borderColor: "#256628",
                              backgroundColor: "#f3faf3",
                            },
                          }}
                        >
                          {sendingCode
                            ? "Wysyłanie kodu..."
                            : "Wyślij kod weryfikacyjny"}
                        </Button>
                      )}

                    {/* WPISANIE KODU */}

                    {showEmailVerifyInput && !emailVerified && (
                      <Box
                        sx={{
                          mt: 2,
                        }}
                      >
                        <Alert
                          severity="info"
                          sx={{
                            mb: 2,
                            borderRadius: 2,
                          }}
                        >
                          Wysłaliśmy kod na <strong>{values.email}</strong>.
                          Sprawdź również folder SPAM.
                        </Alert>

                        <Stack
                          direction={{
                            xs: "column",
                            sm: "row",
                          }}
                          spacing={1}
                        >
                          <TextField
                            label="Kod z e-maila"
                            placeholder="Wpisz kod"
                            value={emailCode}
                            onChange={(e) => setEmailCode(e.target.value)}
                            fullWidth
                            autoFocus
                            inputProps={{
                              style: {
                                textAlign: "center",
                                fontSize: "1.2rem",
                                letterSpacing: "0.15em",
                              },
                            }}
                            sx={fieldSx}
                          />

                          <Button
                            type="button"
                            variant="contained"
                            disabled={verifyingCode || !emailCode}
                            onClick={async () => {
                              setVerifyingCode(true);

                              try {
                                const response = await apiRequest(
                                  "verify-email-code-register",
                                  "POST",
                                  {
                                    email: values.email,
                                    code: emailCode,
                                  },
                                );

                                if (response.success) {
                                  setEmailVerified(true);

                                  setVerifyMessage(
                                    "Adres e-mail został zweryfikowany.",
                                  );
                                } else {
                                  setVerifyMessage(
                                    "Podany kod jest nieprawidłowy.",
                                  );
                                }
                              } catch (error) {
                                console.error(error);

                                setVerifyMessage(
                                  "Nie udało się zweryfikować kodu.",
                                );
                              } finally {
                                setVerifyingCode(false);
                              }
                            }}
                            sx={{
                              minWidth: {
                                sm: 150,
                              },
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 700,
                              backgroundColor: "#2e7d32",

                              "&:hover": {
                                backgroundColor: "#256628",
                              },
                            }}
                          >
                            {verifyingCode ? "Sprawdzanie..." : "Potwierdź kod"}
                          </Button>
                        </Stack>

                        {verifyMessage && (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 1.5,
                              fontWeight: 500,
                              color: emailVerified ? "#2e7d32" : "#ed6c02",
                            }}
                          >
                            {verifyMessage}
                          </Typography>
                        )}

                        <Button
                          type="button"
                          variant="text"
                          disabled={sendingCode}
                          onClick={async () => {
                            setSendingCode(true);

                            try {
                              await apiRequest(
                                "send-email-code-register",
                                "POST",
                                {
                                  email: values.email,
                                },
                              );

                              setVerifyMessage("Nowy kod został wysłany.");
                            } finally {
                              setSendingCode(false);
                            }
                          }}
                          sx={{
                            mt: 1,
                            textTransform: "none",
                            color: "text.secondary",
                          }}
                        >
                          Nie otrzymałem kodu — wyślij ponownie
                        </Button>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 4 }} />

                  {/* =========================================
                      HASŁO
                  ========================================= */}

                  <SectionTitle
                    icon={<LockOutlined />}
                    title="Bezpieczeństwo konta"
                    description="Utwórz hasło, którego będziesz używać podczas logowania."
                  />

                  <Row>
                    <PasswordFields
                      values={values}
                      handleChange={handleChange}
                      errors={errors}
                      touched={touched}
                    />
                  </Row>

                  <Divider sx={{ my: 4 }} />

                  {/* =========================================
                      LOKALIZACJA
                  ========================================= */}

                  <SectionTitle
                    icon={<LocationOnOutlined />}
                    title="Gdzie szukasz opiekunki?"
                    description="Wybierz województwo i miejscowość, w której potrzebujesz pomocy."
                  />

                  <Alert
                    severity="info"
                    icon={<LocationOnOutlined />}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                    }}
                  >
                    Na podstawie lokalizacji będziemy mogli pokazać Ci opiekunki
                    dostępne w Twojej okolicy.
                  </Alert>

                  <Row>
                    <LocationSelect
                      values={values}
                      setFieldValue={setFieldValue}
                      errors={errors}
                      touched={touched}
                    />
                  </Row>

                  <Divider sx={{ my: 4 }} />

                  {/* =========================================
                      RODO
                  ========================================= */}

                  <FormControlLabel
                    sx={{
                      alignItems: "flex-start",
                      m: 0,
                    }}
                    control={
                      <Checkbox
                        name="rodo"
                        checked={values.rodo}
                        onChange={handleChange}
                        color="success"
                        sx={{
                          mt: -0.5,
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.6,
                          color: "#555",
                        }}
                      >
                        Akceptuję zasady przetwarzania danych osobowych opisane
                        w{" "}
                        <a
                          href="/polityka-prywatnosci"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#2e7d32",
                            fontWeight: 600,
                          }}
                        >
                          Polityce prywatności
                        </a>
                        .
                      </Typography>
                    }
                  />

                  {touched.rodo && errors.rodo && (
                    <FormHelperText error>{errors.rodo}</FormHelperText>
                  )}

                  {/* =========================================
                      CAPTCHA
                  ========================================= */}

                  <Box
                    sx={{
                      mt: 3,
                      display: "flex",
                      justifyContent: {
                        xs: "flex-start",
                        sm: "center",
                      },
                      overflowX: "auto",
                    }}
                  >
                    <ReCAPTCHA
                      sitekey="6Ld8dZcsAAAAAI2kdXLZVwduM4ydXysrZRJju4qo"
                      onChange={setCaptchaToken}
                    />
                  </Box>

                  {/* =========================================
                      CTA
                  ========================================= */}

                  <Box
                    sx={{
                      mt: 4,
                      maxWidth: 520,
                      mx: "auto",
                    }}
                  >
                    {!emailVerified && (
                      <Alert
                        severity="warning"
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                        }}
                      >
                        Przed utworzeniem konta zweryfikuj swój adres e-mail.
                      </Alert>
                    )}

                    {(showTextEmail || showTextPhone) && (
                      <Alert
                        severity="warning"
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                        }}
                      >
                        Popraw zaznaczone dane, aby kontynuować rejestrację.
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={
                        isSubmitting ||
                        !emailVerified ||
                        showTextEmail ||
                        showTextPhone
                      }
                      endIcon={!isSubmitting && <ArrowForward />}
                      sx={{
                        py: 1.6,
                        borderRadius: 2.5,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "1.05rem",
                        backgroundColor: "#2e7d32",
                        boxShadow: "0 6px 18px rgba(46,125,50,0.22)",

                        "&:hover": {
                          backgroundColor: "#256628",
                          boxShadow: "0 8px 22px rgba(46,125,50,0.30)",
                        },
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <CircularProgress
                            size={20}
                            color="inherit"
                            sx={{
                              mr: 1,
                            }}
                          />
                          Tworzenie konta...
                        </>
                      ) : (
                        "Załóż bezpłatne konto"
                      )}
                    </Button>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{
                        mt: 1.5,
                        lineHeight: 1.5,
                      }}
                    >
                      Rejestracja jest bezpłatna. Po utworzeniu konta możesz
                      rozpocząć wyszukiwanie opiekunki.
                    </Typography>
                  </Box>

                  {/* =========================================
                      LOGOWANIE
                  ========================================= */}

                  <Box
                    sx={{
                      mt: 4,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Masz już konto?{" "}
                      <Box
                        component="span"
                        onClick={() => navigate("/login")}
                        sx={{
                          color: "#2e7d32",
                          fontWeight: 700,
                          cursor: "pointer",

                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Zaloguj się
                      </Box>
                    </Typography>
                  </Box>
                </Form>
              )}
            </Formik>
          </Paper>

          {/* ===================================================
              STOPKA INFORMACYJNA
          =================================================== */}

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{
              mt: 3,
              px: 2,
              lineHeight: 1.6,
            }}
          >
            Dbamy o bezpieczeństwo Twoich danych. Informacje podane podczas
            rejestracji wykorzystujemy wyłącznie do obsługi konta i
            funkcjonowania serwisu TwojaOpiekunka.pl.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
